import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform, animate, useReducedMotion } from "framer-motion";
import { LuChevronDown, LuZap, LuFileText, LuChevronRight, LuLoader, LuCheck, LuArrowRightLeft } from "react-icons/lu";

function Navbar() {
  const [enOpen, setEnOpen] = useState(false);

  return (
    <nav className="h-[80px] bg-white border-b border-[#E8E8E8] flex items-center px-4 md:px-8 justify-between sticky top-0 z-50">
      <link href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;700&display=swap" rel="stylesheet" />
      <div className="flex items-center gap-12">
        <img src="https://cdn.jiro.build/Random/Pay%20Up.svg" alt="Pay Up Logo" className="h-8 w-auto mix-blend-multiply" />
        <div className="hidden md:flex gap-[4px] text-[#0D211D] font-medium text-[16px]">
          <a href="#" className="flex items-center justify-center h-[32px] px-[12px] py-[4px] rounded-full hover:bg-[rgba(22,51,0,0.20)] transition-colors">
            Personal
          </a>
          <a href="#" className="flex items-center justify-center h-[32px] px-[12px] py-[4px] rounded-full hover:bg-[rgba(22,51,0,0.20)] transition-colors">
            Business
          </a>
          <a href="#" className="flex items-center justify-center h-[32px] px-[12px] py-[4px] rounded-full hover:bg-[rgba(22,51,0,0.20)] transition-colors">
            Platform
          </a>
        </div>
      </div>
      <div className="flex items-center gap-[4px]">
        <div className="relative">
          <button 
            className="flex items-center justify-center gap-2 group h-[32px] px-[12px] py-[4px] rounded-full hover:bg-[rgba(22,51,0,0.20)] transition-colors" 
            onClick={() => setEnOpen(!enOpen)}
            aria-label="Language dropdown"
          >
            <span className="text-[20px] leading-none">🇺🇸</span>
            <span className="font-medium text-[#0D211D] transition-colors tracking-tight">EN</span>
            <LuChevronDown className="w-4 h-4 text-[#0D211D] transition-transform group-hover:rotate-180" />
          </button>
          
          <AnimatePresence>
            {enOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setEnOpen(false)} />
                <motion.div 
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                  className="absolute top-[calc(100%+16px)] left-1/2 -translate-x-1/2 w-[120px] bg-white shadow-[0_14.5px_30.74px_0_rgba(0,0,0,0.10)] border border-[#E8E8E8] rounded-xl py-2 z-50 flex flex-col"
                >
                  <button className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left w-full transition-colors font-medium">🇺🇸 <span className="uppercase text-[#0D0D0D]">EN</span></button>
                  <button className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left w-full transition-colors font-medium">🇪🇸 <span className="uppercase text-[#0D0D0D]">ES</span></button>
                  <button className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left w-full transition-colors font-medium">🇫🇷 <span className="uppercase text-[#0D0D0D]">FR</span></button>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
        <a href="#" className="font-medium text-[#0D211D] items-center justify-center h-[32px] px-[12px] py-[4px] rounded-full hover:bg-[rgba(22,51,0,0.20)] transition-colors hidden md:flex">Help</a>
        <a href="#" className="font-medium text-[#0D211D] items-center justify-center h-[32px] px-[12px] py-[4px] rounded-full hover:bg-[rgba(22,51,0,0.20)] transition-colors hidden md:flex">Log in</a>
        <button className="flex max-h-[32px] px-[12px] py-[3px] justify-center items-center self-stretch rounded-full border border-[#3D3D3D] bg-[#0D0D0D] text-[#46E8D4] text-center font-sans text-[16px] font-semibold leading-[24px] tracking-[-0.176px] hover:scale-[1.03] hover:shadow-[0_4px_12px_rgba(0,0,0,0.15)] active:scale-[0.97] transition-all">
          Sign up
        </button>
      </div>
    </nav>
  )
}

const CURRENCIES = [
  { code: "USD", flag: "🇺🇸", name: "US Dollar", rate: 1 },
  { code: "EUR", flag: "🇪🇺", name: "Euro", rate: 0.86722 },
  { code: "GBP", flag: "🇬🇧", name: "British Pound", rate: 0.75 },
  { code: "CAD", flag: "🇨🇦", name: "Canadian Dollar", rate: 1.35 },
  { code: "AUD", flag: "🇦🇺", name: "Australian Dollar", rate: 1.52 },
];

function AnimatedNumber({ value }) {
  const count = useMotionValue(value);
  
  useEffect(() => {
    const animation = animate(count, value, { duration: 0.4, ease: "easeOut" });
    return animation.stop;
  }, [value, count]);

  const rounded = useTransform(count, (latest) =>
    latest.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );

  return <motion.span>{rounded}</motion.span>;
}

function CurrencySelector({ selected, options, onChange, exclude }) {
  const [open, setOpen] = useState(false);
  const filtered = options.filter((o) => o.code !== exclude?.code);
  
  return (
    <div className="relative">
      <button 
        className="flex items-center gap-[6px] md:gap-[8px] bg-white border border-[#E8E8E8] px-3 py-[6px] rounded-[999px] hover:bg-gray-50 transition-colors shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[#46E8D4] shrink-0"
        onClick={() => setOpen(!open)}
      >
        <span className="text-[20px] md:text-[24px] leading-none">{selected.flag}</span>
        <span className="font-bold text-[#0D0D0D] text-[16px] md:text-[18px] uppercase">{selected.code}</span>
        <LuChevronDown className={`w-4 h-4 text-[#4B4B4B] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: -8 }} 
              animate={{ opacity: 1, scale: 1, y: 0 }} 
              exit={{ opacity: 0, scale: 0.95, y: -8 }}
              transition={{ duration: 0.15 }}
              className="absolute top-[calc(100%+8px)] left-0 w-[240px] bg-white rounded-xl shadow-[0_12px_32px_rgba(0,0,0,0.12)] border border-[#E8E8E8] py-2 z-50 flex flex-col origin-top"
            >
              {filtered.map((opt) => (
                <button 
                  key={opt.code}
                  className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 text-left transition-colors w-full"
                  onClick={() => {
                    onChange(opt);
                    setOpen(false);
                  }}
                >
                  <span className="text-[24px] leading-none">{opt.flag}</span>
                  <div className="flex flex-col">
                    <span className="font-bold text-[#0D0D0D] uppercase leading-tight">{opt.code}</span>
                    <span className="text-sm text-[#4B4B4B]">{opt.name}</span>
                  </div>
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}

function SubmitButton() {
  const [status, setStatus] = useState("idle");

  const handleClick = () => {
    if (status !== "idle") return;
    setStatus("loading");
    setTimeout(() => {
      setStatus("success");
      setTimeout(() => {
        setStatus("idle");
      }, 1000);
    }, 1200);
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full h-[60px] rounded-[999px] font-bold text-[18px] transition-all flex items-center justify-center mt-6 duration-300 ${status === 'success' ? 'bg-[#0D211D] text-[#46E8D4] shadow-md' : 'bg-[#46E8D4] text-[#0D0D0D] hover:bg-[#073634] hover:text-[#46E8D4] hover:scale-[1.02] shadow-sm hover:shadow-[0_8px_16px_-4px_rgba(70,232,212,0.4)]'}`}
    >
      {status === "idle" && "Send money"}
      {status === "loading" && <LuLoader className="w-6 h-6 animate-spin text-[#0D0D0D]" />}
      {status === "success" && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}><LuCheck className="w-8 h-8" /></motion.div>}
    </button>
  );
}

function CurrencyConverter() {
  const [sendAmtStr, setSendAmtStr] = useState("1,000.00");
  const [sendCurr, setSendCurr] = useState(CURRENCIES[0]);
  const [recvCurr, setRecvCurr] = useState(CURRENCIES[1]);
  const [speed, setSpeed] = useState("instant");
  const [feeExpanded, setFeeExpanded] = useState(false);
  const [numericSend, setNumericSend] = useState(1000);

  const [, setIsFocused] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      const val = parseFloat(sendAmtStr.replace(/,/g, ""));
      if (!isNaN(val)) setNumericSend(val);
      else setNumericSend(0);
    }, 200);
    return () => clearTimeout(timer);
  }, [sendAmtStr]);

  const handleBlur = () => {
    setIsFocused(false);
    const val = parseFloat(sendAmtStr.replace(/,/g, ""));
    if (!isNaN(val)) {
      setSendAmtStr(val.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    }
  };

  const rate = recvCurr.rate / sendCurr.rate;
  const computedRecv = numericSend * rate;
  const totalFees = speed === "instant" ? 5.47 : 1.25;

  const handleSwap = () => {
    setSendCurr(recvCurr);
    setRecvCurr(sendCurr);
    const currentRecv = computedRecv;
    setSendAmtStr(currentRecv.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };

  return (
    <motion.section 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className="w-full px-4 mb-[40px] md:mb-[80px] z-20 flex justify-center"
    >
      <div className="w-full max-w-[960px] rounded-[24px] shadow-[0_14.5px_30.74px_0_rgba(0,0,0,0.10)] bg-white overflow-hidden relative">
        
        <div className="bg-[#0D211D] pt-[24px] pb-[48px] pl-[40px] pr-[38px] relative overflow-hidden flex items-start">
          <h3 className="text-[#46E8D4] font-['Inter','Helvetica_Neue',sans-serif] text-[24px] font-normal leading-[1.2] z-10 max-w-[70%]">Send securely to 140+ countries</h3>
          <div className="absolute right-[-20px] md:right-[-40px] top-[-10%] md:top-[-20%] bottom-0 w-[180px] md:w-[320px] bg-white/10 rotate-12 opacity-25 rounded-2xl pointer-events-none" style={{ backdropFilter: "blur(4px)" }}>
             <div className="w-full h-full border-[2px] border-white/20 rounded-2xl relative">
                <div className="absolute top-8 right-8 w-8 md:w-12 h-6 md:h-8 bg-white/20 rounded-[4px]" />
                <div className="absolute top-20 md:top-24 left-8 w-[60%] h-3 md:h-4 bg-white/10 rounded-full" />
             </div>
          </div>
        </div>

        <div className="p-[24px] md:p-[40px] flex flex-col items-start bg-white relative z-10 w-full rounded-t-[24px] -mt-[24px]">
          
          <div className="flex flex-col md:flex-row gap-6 md:gap-8 w-full relative">
            
            <div className="absolute left-[calc(50%-20px)] top-[calc(50%-20px)] z-30 hidden md:flex items-center justify-center mt-[16px] ml-0">
               <button 
                 onClick={handleSwap} 
                 className="bg-white border text-[#0D0D0D] border-[#E8E8E8] w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-[#46E8D4] hover:border-[#46E8D4] hover:text-[#0D0D0D] shadow-sm transition-all group"
                 aria-label="Swap currencies"
               >
                 <LuArrowRightLeft className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
               </button>
            </div>

            <div className="flex-1 flex flex-col bg-white border-0 pr-0 md:pr-10">
               <span className="text-[#4B4B4B] text-[14px] md:text-[15px] mb-[12px] font-medium">You send exactly</span>
               <div className="flex items-center justify-between gap-4 w-full">
                 <CurrencySelector selected={sendCurr} onChange={setSendCurr} exclude={recvCurr} options={CURRENCIES} />
                 <input
                   value={sendAmtStr}
                   onFocus={() => setIsFocused(true)}
                   onBlur={handleBlur}
                   onChange={(e) => setSendAmtStr(e.target.value)}
                   className="w-[120px] md:flex-1 text-right font-['Inter','Helvetica_Neue',sans-serif] text-[32px] md:text-[48px] font-bold leading-[1.2] text-[#0D0D0D] outline-none border-b-2 border-transparent focus:border-[#46E8D4]/30 transition-colors bg-transparent caret-[#46E8D4] placeholder:text-gray-300"
                   placeholder="0.00"
                 />
               </div>
            </div>
            
            <div className="w-full h-[1px] bg-[#E8E8E8] md:hidden my-2" />

            <div className="flex-1 flex flex-col pl-0 md:pl-10">
               <span className="text-[#4B4B4B] text-[14px] md:text-[15px] mb-[12px] font-medium flex justify-between">
                 Recipient gets 
                 <button onClick={handleSwap} className="md:hidden text-[#0D0D0D] bg-gray-100 p-1 rounded-full"><LuArrowRightLeft className="w-3 h-3" /></button>
               </span>
               <div className="flex items-center justify-between gap-4 w-full">
                 <CurrencySelector selected={recvCurr} onChange={setRecvCurr} exclude={sendCurr} options={CURRENCIES} />
                 <div className="text-right font-['Inter','Helvetica_Neue',sans-serif] text-[32px] md:text-[48px] font-bold leading-[1.2] text-[#0D0D0D] overflow-hidden max-w-[180px] md:max-w-none">
                   <AnimatedNumber value={computedRecv} />
                 </div>
               </div>
            </div>
          </div>
          
          <hr className="w-full border-[#E8E8E8] mt-[21px] mb-[24px] md:mb-[32px]" />

          <div className="w-full flex md:gap-8 items-center bg-gray-50/50 rounded-xl md:p-2 mb-[24px] mt-[-9px] ml-0 md:border border-[#E8E8E8]/50 flex-col md:flex-row p-1 border-0">
            <div className="flex bg-gray-100 p-1 rounded-[999px] w-full md:w-[240px] mb-2 md:mb-0">
              <button
                onClick={() => setSpeed("instant")}
                className={`flex-1 py-[8px] rounded-[999px] text-[14px] font-bold transition-all ${speed === "instant" ? "bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] text-[#0D0D0D]" : "text-[#4B4B4B] hover:text-[#0D0D0D]"}`}
              >
                Instant
              </button>
              <button
                onClick={() => setSpeed("standard")}
                className={`flex-1 py-[8px] rounded-[999px] text-[14px] font-bold transition-all ${speed === "standard" ? "bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)] text-[#0D0D0D]" : "text-[#4B4B4B] hover:text-[#0D0D0D]"}`}
              >
                Standard
              </button>
            </div>
            <p className="text-[13px] md:text-sm text-[#4B4B4B] px-2 text-center md:text-left mb-2 md:mb-0">
              {speed === "instant" ? "Fastest delivery, slightly higher fee." : "Save on fees with standard routing."}
            </p>
          </div>

          <div className="flex flex-col gap-6 w-full">
            <div className="flex flex-col md:flex-row gap-6 w-full justify-between items-start md:items-center">
              
              <div className="flex items-center gap-[16px] w-full md:w-auto">
                <div className="w-[44px] h-[44px] rounded-full border border-[#E8E8E8] bg-white flex items-center justify-center shadow-sm shrink-0">
                  <LuZap className={`w-5 h-5 transition-colors ${speed === 'instant' ? 'text-[#46E8D4] drop-shadow-[0_0_8px_rgba(70,232,212,0.8)]' : 'text-[#4B4B4B]'}`} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[14px] text-[#4B4B4B] mb-1">Arrives</span>
                  <span className="font-bold text-[#0D0D0D] text-[16px] leading-none">
                    {speed === "instant" ? "Today - in seconds" : "Tomorrow - standard routing"}
                  </span>
                </div>
              </div>

              <div 
                className="flex items-center gap-[16px] flex-1 md:justify-end group cursor-pointer w-full md:w-auto p-3 -m-3 rounded-xl hover:bg-gray-50 transition-colors" 
                onClick={() => setFeeExpanded(!feeExpanded)}
              >
                <div className="w-[44px] h-[44px] rounded-full border border-[#E8E8E8] bg-white flex items-center justify-center shadow-sm shrink-0">
                  <LuFileText className="w-5 h-5 text-[#0D0D0D]" />
                </div>
                <div className="flex flex-col mr-[8px] md:mr-[16px] flex-1 md:flex-none">
                  <span className="text-[14px] text-[#4B4B4B] mb-1">Total fees</span>
                  <span className="font-bold text-[#0D0D0D] text-[16px] leading-none">Included in {sendCurr.code} amount</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-[16px] text-[#0D0D0D] whitespace-nowrap">{totalFees.toFixed(2)} {sendCurr.code}</span>
                  <div className={`w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center transition-transform duration-300 shrink-0 ${feeExpanded ? "rotate-90 bg-[#46E8D4]/20" : "group-hover:bg-gray-200"}`}>
                    <LuChevronRight className="w-4 h-4 text-[#0D0D0D]" />
                  </div>
                </div>
              </div>
            </div>
            
            <AnimatePresence>
              {feeExpanded && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden w-full flex md:justify-end text-[14px] text-[#4B4B4B]"
                 >
                    <div className="bg-gray-50 px-5 pt-4 pb-5 rounded-[16px] border border-[#E8E8E8] w-full md:w-[320px] flex flex-col gap-3">
                      <div className="font-bold text-[#0D0D0D] mb-1 pb-2 border-b border-[#E8E8E8]/50">Fee Breakdown</div>
                      <div className="flex justify-between items-center">
                        <span>Transfer fee</span> 
                        <span className="font-medium text-[#0D0D0D]">{(totalFees * 0.8).toFixed(2)} {sendCurr.code}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span>Exchange margin</span> 
                        <span className="font-medium text-[#0D0D0D]">{(totalFees * 0.2).toFixed(2)} {sendCurr.code}</span>
                      </div>
                    </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <SubmitButton />
        </div>
      </div>
    </motion.section>
  )
}

export default function Hero() {
  const shouldReduceMotion = useReducedMotion();

  return (
    <>
      <Navbar />
      <section className="relative pt-[112px] pb-[64px] flex flex-col items-center text-center px-4 w-full max-w-[1200px] mx-auto z-10">
      
      <motion.div
        initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, x: -20, rotate: -10 }}
        animate={shouldReduceMotion ? { opacity: 1 } : { opacity: 1, x: 0, rotate: -4 }}
        transition={shouldReduceMotion ? { duration: 0.3 } : { duration: 0.45, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        className="hidden lg:block absolute top-[100px] left-[5%] z-10 -ml-[70px] -mt-[40px]"
      >
        <motion.img
          src="https://cdn.jiro.build/Random/426864600_2f51efba-4dda-4ba3-bb5f-ba1b068f7230%201.png"
          alt="Wallet"
          animate={{ y: [-4, 4, -4] }}
          transition={{ duration: 3, ease: "easeInOut", repeat: Infinity }}
          className="w-[140px] h-auto mix-blend-multiply drop-shadow-[0_14px_24px_rgba(0,0,0,0.15)]"
          aria-hidden="true"
        />
      </motion.div>

      <motion.div
        initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -40, rotate: 0, filter: "drop-shadow(0px 14px 24px rgba(0,0,0,0))" }}
        animate={shouldReduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, rotate: 22, filter: "drop-shadow(0px 14px 24px rgba(0,0,0,0.15))" }}
        transition={shouldReduceMotion ? { duration: 0.3 } : { duration: 0.55, delay: 0.65, ease: [0.16, 1, 0.3, 1] }}
        className="hidden lg:block absolute top-[100px] right-[5%] z-[99] -mt-[25px] mr-[78px]"
      >
        <motion.img
          src="https://cdn.jiro.build/Random/economy-concept-with-copy-space%201.png"
          alt="Rolled dollar bill"
          animate={{ y: [-4, 4, -4] }}
          transition={{ duration: 3.5, ease: "easeInOut", repeat: Infinity, delay: 0.5 }}
          className="w-[180px] h-auto"
          aria-hidden="true"
        />
      </motion.div>

      <div className="font-['Anton',sans-serif] text-[64px] sm:text-[90px] md:text-[112px] leading-[1.07] uppercase text-[#0D0D0D] max-w-[1024px] relative z-20 flex flex-col items-center">
         
         <div className="flex flex-wrap justify-center gap-x-[12px] sm:gap-x-[20px] md:gap-x-[28px] overflow-visible">
            {["SEND,", "PAY", "&", "EXCHANGE"].map((word, i) => (
              <span key={i} className="overflow-hidden inline-flex pt-[4px] sm:pt-[8px] pb-[4px] sm:pb-[8px] -mt-[4px] sm:-mt-[8px] -mb-[4px] sm:-mb-[8px]">
                <motion.span
                  className="inline-block relative"
                  initial={shouldReduceMotion ? { opacity: 0 } : { y: "110%" }}
                  animate={shouldReduceMotion ? { opacity: 1 } : { y: "0%" }}
                  transition={shouldReduceMotion ? { duration: 0.3, delay: 0.2 } : { duration: 0.55, ease: [0.16, 1, 0.3, 1], delay: 0.2 + (i * 0.06) }}
                >
                  {word}
                </motion.span>
              </span>
            ))}
         </div>
         
         <div className="flex items-center justify-center gap-[6px] sm:gap-[16px] flex-wrap mt-[4px] sm:mt-[8px]">
           <span className="overflow-hidden inline-flex pt-[4px] sm:pt-[8px] pb-[4px] sm:pb-[8px] -mt-[4px] sm:-mt-[8px] -mb-[4px] sm:-mb-[8px]">
             <motion.span
               className="inline-block relative"
               initial={shouldReduceMotion ? { opacity: 0 } : { y: "110%" }}
               animate={shouldReduceMotion ? { opacity: 1 } : { y: "0%" }}
               transition={shouldReduceMotion ? { duration: 0.3, delay: 0.2 } : { duration: 0.55, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
             >
               MONEY
             </motion.span>
           </span>
           
           <motion.div
             initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.4, rotate: -25 }}
             animate={shouldReduceMotion ? { opacity: 1 } : { opacity: 1, scale: [0.4, 1.1, 1], rotate: 0 }}
             transition={shouldReduceMotion ? { duration: 0.3, delay: 0.2 } : { duration: 0.4, delay: 0.75, ease: [0.34, 1.56, 0.64, 1] }}
             className="inline-flex items-center justify-center mx-[4px] sm:mx-[0]"
           >
             <video
               src="https://cdn.jiro.build/Random/Coin%20Hero.mp4"
               autoPlay
               loop
               muted
               playsInline
               className="w-[90px] h-[90px] sm:w-[110px] sm:h-[110px] md:w-[135px] md:h-[135px] object-cover mix-blend-multiply rounded-[99px]"
               aria-hidden="true"
             />
           </motion.div>

           <span className="overflow-hidden inline-flex pt-[4px] sm:pt-[8px] pb-[4px] sm:pb-[8px] -mt-[4px] sm:-mt-[8px] -mb-[4px] sm:-mb-[8px]">
             <motion.span
               className="inline-block relative"
               initial={shouldReduceMotion ? { opacity: 0 } : { y: "110%" }}
               animate={shouldReduceMotion ? { opacity: 1 } : { y: "0%" }}
               transition={shouldReduceMotion ? { duration: 0.3, delay: 0.2 } : { duration: 0.55, ease: [0.16, 1, 0.3, 1], delay: 0.56 }}
             >
               WORLDWIDE
             </motion.span>
           </span>
         </div>
      </div>

      <motion.p
        initial={shouldReduceMotion ? { opacity: 0 } : { y: 20, opacity: 0 }} 
        animate={shouldReduceMotion ? { opacity: 1 } : { y: 0, opacity: 1 }} 
        transition={shouldReduceMotion ? { duration: 0.3, delay: 0.2 } : { duration: 0.5, delay: 0.85 }}
        className="mt-[16px] pt-0 ml-0 text-[18px] md:text-[22px] leading-[1.45] font-medium text-[#0D0D0D] w-full max-w-[795px] font-['Inter','Helvetica_Neue',sans-serif]"
      >
        Move your money where it matters. Save on international transfers in over 50 currencies, without any hidden fees.
      </motion.p>

      <motion.button
        initial={shouldReduceMotion ? { opacity: 0 } : { y: 20, opacity: 0 }} 
        animate={shouldReduceMotion ? { opacity: 1 } : { y: 0, opacity: 1 }} 
        transition={shouldReduceMotion ? { duration: 0.3, delay: 0.2 } : { duration: 0.5, delay: 0.95 }}
        className="mt-[40px] inline-flex items-center justify-center gap-[10px] px-[40px] py-[14px] bg-[#46E8D4] text-black font-['Inter','Helvetica_Neue',sans-serif] font-medium text-[22px] leading-[32px] text-center rounded-[999px] shadow-sm hover:shadow-[0_8px_20px_-4px_rgba(70,232,212,0.5)] transition-all relative z-30 hover:bg-[#073634] hover:text-[#46E8D4] hover:scale-[1.05] active:scale-[0.97]"
      >
        Open an account
      </motion.button>
    </section>
    <CurrencyConverter />
    </>
  )
}